﻿/**
 * Push notification service for mobile devices
 * Handles price alerts, volatility warnings, and other notifications
 */

class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window
    this.isSubscribed = false
    this.subscription = null
    this.permission = 'default'
    this.doNotDisturbMode = false
    this.notificationGroups = new Map()
    this.priorities = {
      HIGH: 'high',
      NORMAL: 'normal',
      LOW: 'low'
    }
    this.serviceWorkerRegistration = null
    this.vapidPublicKey = import.meta.env.VITE_PWA_VAPID_PUBLIC_KEY || import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
    this.serverSubscriptionSynced = false
  }

  async initialize() {
    if (!this.isSupported) {
      console.warn('Push notifications not supported')
      return false
    }

    // Check current permission
    this.permission = Notification.permission

    // Register service worker if not already registered
    if ('serviceWorker' in navigator) {
      try {
        // 中文注释：Vite 可能部署在子路径下，Service Worker 必须跟随 BASE_URL，否则 PWA 安装后离线缓存会偏到根路径。
        const basePath = import.meta.env.BASE_URL || '/'
        const swUrl = `${basePath.replace(/\/$/, '')}/sw.js?v=6`.replace('//sw.js', '/sw.js')
        const scope = basePath.endsWith('/') ? basePath : `${basePath}/`
        const registration = await navigator.serviceWorker.register(swUrl, { scope })
        this.serviceWorkerRegistration = registration
        console.log('Service Worker registered:', registration)
      } catch (error) {
        console.error('Service Worker registration failed:', error)
        return false
      }
    }

    // Check if already subscribed
    await this.checkSubscription()

    return true
  }

  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Push notifications not supported')
    }

    if (this.permission === 'granted') {
      // 中文注释：用户可能之前已经允许通知，但还没有创建 Web Push 订阅；按钮点击时仍要补订阅。
      if (!this.isSubscribed || !this.serverSubscriptionSynced) {
        await this.subscribe()
      }
      return true
    }

    try {
      const permission = await Notification.requestPermission()
      this.permission = permission
      
      if (permission === 'granted') {
        await this.subscribe()
        return true
      } else {
        throw new Error('Notification permission denied')
      }
    } catch (error) {
      console.error('Permission request failed:', error)
      throw error
    }
  }

  async subscribe() {
    if (!this.isSupported || this.permission !== 'granted') {
      throw new Error('Cannot subscribe: permission not granted')
    }

    try {
      const registration = await navigator.serviceWorker.ready
      
      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription()
      if (existingSubscription) {
        this.subscription = existingSubscription
        // 中文注释：浏览器本地已有订阅时，也要补发给后端，避免 Redis 过期或重建后收不到 PWA 推送。
        await this.sendSubscriptionToServer(existingSubscription)
        this.isSubscribed = true
        this.serverSubscriptionSynced = true
        return existingSubscription
      }

      // Create new subscription
      const vapidPublicKey = await this.getVapidPublicKey()
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      })

      // Send subscription to server
      await this.sendSubscriptionToServer(subscription)

      this.subscription = subscription
      this.isSubscribed = true
      this.serverSubscriptionSynced = true

      return subscription
    } catch (error) {
      console.error('Subscription failed:', error)
      throw error
    }
  }

  async unsubscribe() {
    if (!this.subscription) {
      return true
    }

    try {
      await this.subscription.unsubscribe()
      
      // Remove from server
      await this.removeSubscriptionFromServer(this.subscription)
      
      this.subscription = null
      this.isSubscribed = false
      this.serverSubscriptionSynced = false
      
      return true
    } catch (error) {
      console.error('Unsubscribe failed:', error)
      throw error
    }
  }

  async checkSubscription(options = {}) {
    if (!this.isSupported) return false

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      
      if (subscription) {
        this.subscription = subscription
        this.isSubscribed = true
        if (options.syncServer) {
          try {
            // 中文注释：移动端重进页面时把本地订阅补发到后端，修复 Redis 重建或早期订阅保存失败后收不到推送的问题。
            await this.sendSubscriptionToServer(subscription)
            this.serverSubscriptionSynced = true
          } catch (syncError) {
            this.serverSubscriptionSynced = false
            console.warn('Sync existing subscription failed:', syncError)
          }
        }
        return true
      }

      this.subscription = null
      this.isSubscribed = false
      this.serverSubscriptionSynced = false
      
      return false
    } catch (error) {
      console.error('Check subscription failed:', error)
      return false
    }
  }

  // Local notifications (fallback for when push is not available)
  showLocalNotification(title, options = {}) {
    if (!this.isSupported || this.permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted')
      return null
    }

    if (this.doNotDisturbMode && options.priority !== this.priorities.HIGH) {
      console.log('Notification suppressed: Do Not Disturb mode active')
      return null
    }

    const defaultOptions = {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/badge-72x72.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      ...options
    }

    // Group notifications
    if (options.group) {
      this.groupNotification(title, defaultOptions)
      return null
    }

    try {
      const notification = new Notification(title, defaultOptions)
      
      // Auto-close after delay
      if (options.autoClose !== false) {
        setTimeout(() => {
          notification.close()
        }, options.duration || 5000)
      }

      return notification
    } catch (error) {
      console.error('Show notification failed:', error)
      return null
    }
  }

  // Notification types for stock trading
  showPriceAlert(stockCode, currentPrice, targetPrice, direction) {
    const title = `价格提醒 - ${stockCode}`
    const body = direction === 'up' 
      ? `${stockCode} 已涨至 ¥${currentPrice}，达到目标价 ¥${targetPrice}`
      : `${stockCode} 已跌至 ¥${currentPrice}，达到目标价 ¥${targetPrice}`

    return this.showLocalNotification(title, {
      body,
      icon: '/icons/price-alert.png',
      tag: `price-alert-${stockCode}`,
      priority: this.priorities.HIGH,
      requireInteraction: true,
      actions: [
        { action: 'view', title: '查看详情' },
        { action: 'trade', title: '立即交易' }
      ],
      data: {
        type: 'price-alert',
        stockCode,
        currentPrice,
        targetPrice,
        direction
      }
    })
  }

  showVolatilityWarning(stockCode, changePercent, riskLevel) {
    const title = `异动警告 - ${stockCode}`
    const body = `${stockCode} 波动异常，涨跌幅 ${changePercent > 0 ? '+' : ''}${changePercent.toFixed(2)}%`

    const riskColors = {
      low: '#fbbf24',
      medium: '#f59e0b',
      high: '#ef4444'
    }

    return this.showLocalNotification(title, {
      body,
      icon: '/icons/volatility-warning.png',
      tag: `volatility-${stockCode}`,
      priority: riskLevel === 'high' ? this.priorities.HIGH : this.priorities.NORMAL,
      vibrate: riskLevel === 'high' ? [300, 200, 300] : [200, 100, 200],
      actions: [
        { action: 'view', title: '查看详情' },
        { action: 'adjust', title: '调整策略' }
      ],
      data: {
        type: 'volatility-warning',
        stockCode,
        changePercent,
        riskLevel
      }
    })
  }

  showTradeSignal(stockCode, signal, confidence) {
    const title = `交易信号 - ${stockCode}`
    const signalText = {
      buy: '买入',
      sell: '卖出',
      hold: '持有'
    }
    
    const body = `${stockCode} ${signalText[signal]}信号，置信度 ${(confidence * 100).toFixed(0)}%`

    return this.showLocalNotification(title, {
      body,
      icon: '/icons/trade-signal.png',
      tag: `signal-${stockCode}`,
      priority: confidence > 0.8 ? this.priorities.HIGH : this.priorities.NORMAL,
      actions: [
        { action: 'execute', title: '执行交易' },
        { action: 'ignore', title: '忽略信号' }
      ],
      data: {
        type: 'trade-signal',
        stockCode,
        signal,
        confidence
      }
    })
  }

  showSystemNotification(title, message, type = 'info') {
    const icons = {
      info: '/icons/info.png',
      success: '/icons/success.png',
      warning: '/icons/warning.png',
      error: '/icons/error.png'
    }

    return this.showLocalNotification(title, {
      body: message,
      icon: icons[type] || icons.info,
      tag: `system-${type}`,
      priority: type === 'error' ? this.priorities.HIGH : this.priorities.NORMAL,
      autoClose: type !== 'error'
    })
  }

  // Notification grouping
  groupNotification(title, options) {
    const groupKey = options.group
    
    if (!this.notificationGroups.has(groupKey)) {
      this.notificationGroups.set(groupKey, [])
    }
    
    const group = this.notificationGroups.get(groupKey)
    group.push({ title, options })
    
    // Show grouped notification after delay
    setTimeout(() => {
      this.showGroupedNotification(groupKey)
    }, 1000)
  }

  showGroupedNotification(groupKey) {
    const group = this.notificationGroups.get(groupKey)
    if (!group || group.length === 0) return

    if (group.length === 1) {
      const { title, options } = group[0]
      this.showLocalNotification(title, { ...options, group: undefined })
    } else {
      const title = `${group.length} 条新通知`
      const body = group.map(item => item.title).join(', ')
      
      this.showLocalNotification(title, {
        body,
        tag: `group-${groupKey}`,
        data: { type: 'grouped', groupKey, items: group }
      })
    }
    
    this.notificationGroups.delete(groupKey)
  }

  // Do Not Disturb mode
  enableDoNotDisturb(duration = null) {
    this.doNotDisturbMode = true
    
    if (duration) {
      setTimeout(() => {
        this.doNotDisturbMode = false
      }, duration)
    }
  }

  disableDoNotDisturb() {
    this.doNotDisturbMode = false
  }

  isDoNotDisturbActive() {
    return this.doNotDisturbMode
  }

  // Smart notification scheduling
  shouldShowNotification(options = {}) {
    // Check Do Not Disturb mode
    if (this.doNotDisturbMode && options.priority !== this.priorities.HIGH) {
      return false
    }

    // Check time-based rules (e.g., quiet hours)
    const now = new Date()
    const hour = now.getHours()
    
    // Quiet hours: 22:00 - 08:00 (except high priority)
    if ((hour >= 22 || hour < 8) && options.priority !== this.priorities.HIGH) {
      return false
    }

    // Check frequency limits
    if (options.tag && this.isNotificationTooFrequent(options.tag)) {
      return false
    }

    return true
  }

  isNotificationTooFrequent(tag) {
    // Simple frequency check - could be enhanced with more sophisticated logic
    const lastShown = localStorage.getItem(`notification-${tag}`)
    if (!lastShown) return false
    
    const timeSinceLastShown = Date.now() - parseInt(lastShown)
    const minInterval = 5 * 60 * 1000 // 5 minutes
    
    return timeSinceLastShown < minInterval
  }

  // Utility methods
  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey
    }

    // 中文注释：公钥允许运行时读取，部署时只换后端环境变量，不需要重新打前端包。
    const response = await fetch('/api/push/vapid-public-key')
    if (!response.ok) {
      throw new Error('Failed to load VAPID public key')
    }
    const result = await response.json()
    this.vapidPublicKey = result?.data?.public_key || ''
    if (!this.vapidPublicKey) {
      throw new Error('PWA VAPID public key is empty')
    }
    return this.vapidPublicKey
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  async sendSubscriptionToServer(subscription) {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          userAgent: navigator.userAgent
        })
      })

      if (!response.ok) {
        throw new Error('Failed to send subscription to server')
      }
    } catch (error) {
      console.error('Send subscription failed:', error)
      throw error
    }
  }

  async removeSubscriptionFromServer(subscription) {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      })

      if (!response.ok) {
        throw new Error('Failed to remove subscription from server')
      }
    } catch (error) {
      console.error('Remove subscription failed:', error)
      throw error
    }
  }

  // Getters
  getPermission() {
    return this.permission
  }

  getSubscription() {
    return this.subscription
  }

  isNotificationSupported() {
    return this.isSupported
  }

  isUserSubscribed() {
    return this.isSubscribed
  }

  isServerSubscriptionSynced() {
    return this.serverSubscriptionSynced
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService()

export default pushNotificationService
